import { useState, useContext, useEffect } from "react"
import { useNavigate } from "react-router-dom"

import { Button, FormControl, FormControlLabel, RadioGroup, Radio, Dialog, DialogActions, DialogContent, DialogTitle, Typography, Alert, Checkbox } from "@mui/material"
import { enqueueSnackbar } from 'notistack'

import FlareRoundedIcon from '@mui/icons-material/FlareRounded'
import EastRoundedIcon from '@mui/icons-material/EastRounded'

import { AppContext } from "/providers/app-context"
import { changePlan } from '/providers/studio-api'
import PopupShell from '../editor/popup-shell'


const changePlanDialog = ({ open, setOpen, client, nextBillingDate, prepay, currentPlanName, applyForMonthlyContract }) => {

  const { apiSource, apiKeys, setProcessing, refreshClient } = useContext(AppContext)
  const navigate = useNavigate()

  const [selectedPlan, setSelectedPlan] = useState("FREE")
  const [noCardError, setNoCardError] = useState(false)
  const [downgraded, setDowngraded] = useState(false)
  const [newMaxPasses, setNewMaxPasses] = useState(0)
  const [agreed, setAgreed] = useState(false)
  const [termsError, setTermsError] = useState("")

  useEffect(() => {
    if (client) {
      if (client.next_tariff_tier) setSelectedPlan(client.next_tariff_tier)
      else setSelectedPlan(client.tariff_tier)
      setAgreed(false)
      setTermsError("")
    }
  }, [client, open])

  useEffect(() => {
    if (!open) {
      setDowngraded(false)
      setNewMaxPasses(0)
    }
  }, [open])

    useEffect(() => {
    if (!client?.stripe_card_payment_method_id) {
      setNoCardError(true)
    }
  }, [client])

  const saveNewPlan = async () => {

    // Does client have a stripe customer ID?
    // If not then they must provide card details first (and pay this month's fee)
    // Also change billing day to today. (start new billing period)... How does this affect current / peak usage count?

    if (!agreed) {
      setTermsError("You must agree to the prepay plan terms and conditions before changing plan.")
      return
    }
    if (termsError) setTermsError("")

    if (client.stripe_card_payment_method_id) {
      enqueueSnackbar({ message: 'Saving new plan', variant: 'light' })
      setProcessing(true)
      const result = await changePlan({ apiSource, apiKeys, new_tier: selectedPlan })
      setProcessing(false)

      if (result.ok) {
        enqueueSnackbar({ message: 'New plan saved', variant: 'success' })
        await refreshClient()

        if (result.changeType === "downgrade") {
          setDowngraded(true)
          setNewMaxPasses(result.maxPasses)
        } else {
          close()
        }
      } else {
        enqueueSnackbar({ message: 'Plan change failed. ' + result.data.message, variant: 'error' })
      }

    } else { // No payment setup for this client. Need to go through process to add card and take initial payment.
      setNoCardError(true)
    }
  }

  const close = () => setOpen(false)

  if (downgraded) return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <Typography variant="h3">Plan scheduled for downgrade</Typography>
      </DialogTitle>

      <DialogContent>
        <div className="flex flex-col gap-2">
          <div>Your plan is now scheduled to change to <span className="font-semibold text-xl">{selectedPlan}</span> on <span className="font-semibold text-xl">{nextBillingDate}</span></div>
          <div>It is your responsibility to delete any necessary passes in order to bring your current active pass count to <span className="font-semibold text-xl">{newMaxPasses}</span> or below.</div>
          <div>If you have more than <span className="font-semibold text-xl">{newMaxPasses}</span> active passes on <span className="font-semibold text-xl">{nextBillingDate}</span> then we will be unable to action your downgrade.</div>
          <div>In this event you will remain on the <span className="font-semibold text-xl">{currentPlanName}</span> plan unless we are able to downgrade you to an intermediate plan.</div>
        </div>
        <hr />
      </DialogContent>

      <DialogActions>
        <div className="flex flex-row justify-start w-full p-5">
          <Button variant="outlined" onClick={close} autoFocus >Close</Button>
        </div>
      </DialogActions>
    </Dialog>
  )

  return (
    <PopupShell open={open} onClose={close} width={520} variant="solid">
      <div className="flex flex-col gap-5">
        <div className="flex flex-row justify-between">
          <Typography color="secondary" variant="h6">Choose a new plan</Typography>
          <Button endIcon={<EastRoundedIcon />} onClick={close} >cancel</Button>
        </div>

        <div className="-mt-5">
          <FormControl>
            <RadioGroup value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}>
              {prepay.map(i => <FormControlLabel key={i.active_passes} disabled={i.tariff_tier !== "FREE" && !client?.stripe_card_payment_method_id} value={i.tariff_tier} control={<Radio />} label={<div className="flex flex-row justify-between w-[300px]"><div>{i.tariff_tier} ({i.active_passes})</div><div>{i.price ? "£" + i.price : "FREE"}</div></div>} />)}
            </RadioGroup>
          </FormControl>
        </div>

        <hr className="-mt-[10px]"/>

        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => {
                  setAgreed(e.target.checked)
                  if (termsError && e.target.checked) setTermsError("")
                }}
              />
            }
            label={
              <span>
                I have read and agree to the{' '}
                <a
                  href="https://studio.smartix.uk/prepay-plans-terms-and-conditions.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 underline"
                >
                  prepay plan terms and conditions
                </a>.
              </span>
            }
          />
          {termsError ? <Typography color="error" variant="body2">{termsError}</Typography> : null}
        </div>

        <div className="flex flex-row justify-between w-full">
          <div>You can close your account<Button onClick={() => navigate("../../account-management/delete")}>here</Button></div>
          <Button variant="contained" onClick={saveNewPlan} disabled={selectedPlan === (client?.next_tariff_tier ? client.next_tariff_tier : client?.tariff_tier)}>Change Plan</Button>
        </div>

        {noCardError && <>
        <Alert severity="error">You have not registered a payment card. You can only choose the FREE tier until you do.
          <div className="flex flex-row justify-end mt-10">
            <Button variant="outlined" onClick={() => navigate("../payment-card")}>Setup a payment card</Button>  
          </div>
        </Alert>
        </>
        }

      </div>

      <div className="italic text-xl mt-[50px]"><FlareRoundedIcon color="secondary" sx={{ fontSize: 40, marginRight: 2 }} />Or complete an application to move to our <span className="font-semibold text-xl">monthly rolling plan</span> <Button onClick={applyForMonthlyContract} variant="outlined">Apply now</Button></div>
    </PopupShell>
  )
}

export default changePlanDialog
